import { useEffect, useState, useCallback } from "react";
import {
  Users, ShoppingCart, BadgeCheck, TrendingUp, Euro, RefreshCw, Copy, PlugZap,
  CheckCircle2, AlertTriangle, Clock, Eye, Package,
} from "lucide-react";
import { toast } from "sonner";
import {
  getAuditSummary, getSessions, getSessionDetail,
  getConversionSettings, saveConversionSettings, registerWebhook,
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { StatTile, codeToFlag } from "@/pages/admin/analyticsUi";

const fmtDuration = (ms) => {
  if (!ms || ms < 1000) return "<1s";
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${s % 60}s`;
};

const fmtMoney = (amount, currency) => {
  if (amount == null) return "—";
  try {
    return new Intl.NumberFormat("en-IE", { style: "currency", currency: currency || "EUR" }).format(amount);
  } catch {
    return `${amount} ${currency || ""}`;
  }
};

const fmtTime = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

const StatusBadge = ({ converted, checkout }) => {
  if (converted) return <Badge className="bg-chart-2 text-white hover:bg-chart-2">Converted</Badge>;
  if (checkout) return <Badge variant="outline" className="border-primary text-primary">Checkout</Badge>;
  return <Badge variant="secondary">Browsing</Badge>;
};

// ---- Conversion setup (webhook) panel ----
const ConversionSetup = () => {
  const [cfg, setCfg] = useState(null);
  const [secret, setSecret] = useState("");
  const [saving, setSaving] = useState(false);
  const [registering, setRegistering] = useState(false);

  const load = useCallback(async () => {
    try { setCfg(await getConversionSettings()); } catch { /* ignore */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await saveConversionSettings({ secret });
      toast.success("Conversion tracking is now active", {
        description: "New Shopify orders will be matched to their visitor session automatically.",
      });
      setSecret("");
      load();
    } catch { toast.error("Could not save secret"); }
    finally { setSaving(false); }
  };

  const register = async () => {
    setRegistering(true);
    try {
      const res = await registerWebhook();
      if (res?.ok === false) {
        toast.error("Auto-register unavailable", {
          description: res.hint || res.error || "Use the manual signing secret instead.",
        });
      } else {
        toast.success(res?.reused ? "Webhook already registered" : "Webhook registered with Shopify");
      }
      load();
    } catch (e) {
      toast.error("Could not register webhook", {
        description: e?.response?.data?.detail || "Use the manual signing-secret method on the right.",
      });
    } finally { setRegistering(false); }
  };

  const copyUrl = () => {
    if (cfg?.webhook_url) {
      navigator.clipboard.writeText(cfg.webhook_url);
      toast.success("Webhook URL copied");
    }
  };

  const ready = !!(cfg?.secret_set || cfg?.webhook_registered);

  return (
    <div className="rounded-xl border border-border bg-card p-5" data-testid="conversion-setup">
      <div className="flex items-center gap-2">
        <PlugZap className="h-5 w-5 text-primary" />
        <h3 className="text-sm font-semibold">Conversion tracking setup</h3>
        {ready ? (
          <Badge className="ml-auto bg-chart-2 text-white hover:bg-chart-2" data-testid="conversion-status"><CheckCircle2 className="mr-1 h-3 w-3" /> Conversion tracking ready</Badge>
        ) : (
          <Badge variant="outline" className="ml-auto border-amber-500 text-amber-500" data-testid="conversion-status"><AlertTriangle className="mr-1 h-3 w-3" /> Setup needed</Badge>
        )}
      </div>

      {ready ? (
        <p className="mt-2 text-xs text-muted-foreground">
          Your signing secret is saved, so incoming Shopify orders are verified and matched to their
          visitor session automatically. (Manually-created webhooks don't appear in Shopify's API list,
          so this still shows as "ready" rather than "registered" — that's expected.)
        </p>
      ) : (
        <p className="mt-2 text-xs text-muted-foreground">
          Two-minute setup: create the order webhook in Shopify, then paste its signing secret below.
        </p>
      )}

      {/* Step 1 — webhook URL */}
      <div className="mt-4 space-y-1.5">
        <Label className="text-xs font-medium text-foreground">Step 1 \u00b7 Create the webhook in Shopify</Label>
        <p className="text-xs text-muted-foreground">
          Shopify Admin \u2192 Settings \u2192 Notifications \u2192 Webhooks \u2192 <strong>Create webhook</strong>,
          event <strong>Order creation</strong>, format <strong>JSON</strong>, and paste this URL:
        </p>
        <div className="flex items-center gap-2">
          <code className="flex-1 truncate rounded bg-secondary px-2 py-1.5 font-mono text-xs" data-testid="webhook-url">{cfg?.webhook_url || "\u2014"}</code>
          <Button variant="ghost" size="icon" onClick={copyUrl} aria-label="Copy webhook URL" data-testid="copy-webhook-url"><Copy className="h-3.5 w-3.5" /></Button>
        </div>
      </div>

      {/* Step 2 — signing secret (primary action) */}
      <div className="mt-4 space-y-1.5">
        <Label htmlFor="wh-secret" className="text-xs font-medium text-foreground">
          Step 2 \u00b7 Paste the signing secret {cfg?.secret_set && <span className="font-normal text-chart-2">(saved: {cfg.secret_preview})</span>}
        </Label>
        <p className="text-xs text-muted-foreground">Shopify shows this right after you create the webhook ("Your webhooks will be signed with \u2026").</p>
        <div className="flex items-center gap-2">
          <Input id="wh-secret" type="password" placeholder={cfg?.secret_set ? "Paste a new secret to replace the saved one" : "Paste the signing secret here"} value={secret} onChange={(e) => setSecret(e.target.value)} data-testid="webhook-secret-input" />
          <Button onClick={save} disabled={saving || !secret} data-testid="save-webhook-secret">Save</Button>
        </div>
      </div>

      {/* Optional auto-register (advanced) */}
      <div className="mt-4 border-t border-border pt-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs text-muted-foreground">
            Optional: skip the manual steps if your Shopify app has the <code className="rounded bg-secondary px-1">read_orders</code> scope.
          </p>
          <Button variant="ghost" size="sm" onClick={register} disabled={registering || !cfg?.store_connected} className="text-xs" data-testid="register-webhook-button">
            <PlugZap className={`mr-1.5 h-3.5 w-3.5 ${registering ? "animate-pulse" : ""}`} /> Try auto-register
          </Button>
        </div>
      </div>
    </div>
  );
};

// ---- Session detail drill-down ----
const SessionDetailDialog = ({ sessionId, open, onOpenChange }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !sessionId) return;
    setLoading(true);
    getSessionDetail(sessionId)
      .then(setDetail)
      .catch(() => toast.error("Could not load session"))
      .finally(() => setLoading(false));
  }, [open, sessionId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto" data-testid="session-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-mono text-sm">{sessionId}</DialogTitle>
          <DialogDescription>Full auditable visitor journey for this session.</DialogDescription>
        </DialogHeader>

        {loading || !detail ? (
          <div className="space-y-3">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-40 w-full" />
          </div>
        ) : (
          <div className="space-y-5">
            {/* Status + order */}
            <div className="flex flex-wrap items-center gap-2">
              <StatusBadge converted={detail.converted} checkout={detail.checkout_reached} />
              {detail.converted && (
                <span className="text-sm font-medium" data-testid="detail-order-info">
                  Order {detail.order_name || "—"} · {fmtMoney(detail.order_total, detail.order_currency)}
                </span>
              )}
            </div>

            {/* Facts grid */}
            <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-3">
              <Fact label="IP address" value={detail.ip} mono />
              <Fact label="Location" value={[detail.city, detail.region, detail.country].filter(Boolean).join(", ")} />
              <Fact label="ISP" value={detail.isp} />
              <Fact label="Device" value={detail.device} />
              <Fact label="Duration" value={fmtDuration(detail.duration_ms)} />
              <Fact label="Pageviews" value={detail.pageviews} />
              <Fact label="Channel" value={detail.channel} />
              <Fact label="Source" value={detail.source} />
              <Fact label="Campaign" value={detail.utm_campaign} />
              <Fact label="Referrer" value={detail.referrer_host} />
              <Fact label="First seen" value={fmtTime(detail.first_seen_at)} />
              <Fact label="Customer email" value={detail.customer_email} />
            </div>

            {/* Products browsed */}
            {detail.products?.length > 0 && (
              <div>
                <h4 className="mb-2 flex items-center gap-1.5 text-sm font-semibold"><Package className="h-4 w-4" /> Products browsed ({detail.products.length})</h4>
                <div className="flex flex-wrap gap-2">
                  {detail.products.map((p, i) => (
                    <Badge key={i} variant="secondary" className="font-normal">{p.title || p.handle}</Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Journey timeline */}
            <div>
              <h4 className="mb-2 flex items-center gap-1.5 text-sm font-semibold"><Clock className="h-4 w-4" /> Page journey</h4>
              <ol className="space-y-2" data-testid="session-journey">
                {(detail.paths || []).map((p, i) => (
                  <li key={i} className="flex items-start gap-3 rounded-lg border border-border bg-background/50 px-3 py-2">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-secondary font-mono text-[10px]">{i + 1}</span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">{p.title || p.path}</p>
                      <p className="truncate font-mono text-xs text-muted-foreground">{p.path}</p>
                    </div>
                    <span className="whitespace-nowrap text-xs text-muted-foreground">{p.ts ? new Date(p.ts).toLocaleTimeString() : ""}</span>
                  </li>
                ))}
                {(!detail.paths || detail.paths.length === 0) && (
                  <li className="text-sm text-muted-foreground">No page hops recorded.</li>
                )}
              </ol>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const Fact = ({ label, value, mono }) => (
  <div>
    <p className="text-xs text-muted-foreground">{label}</p>
    <p className={`truncate ${mono ? "font-mono text-xs" : "text-sm"}`}>{value || "—"}</p>
  </div>
);

export const VisitorAudit = ({ days = 30, includeBots = false }) => {
  const [summary, setSummary] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("recent");
  const [convertedOnly, setConvertedOnly] = useState(false);
  const [checkoutOnly, setCheckoutOnly] = useState(false);
  const [activeSession, setActiveSession] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const base = { days: Number(days), include_bots: includeBots };
      const [sm, ss] = await Promise.all([
        getAuditSummary(base),
        getSessions({ ...base, sort, converted_only: convertedOnly, checkout_only: checkoutOnly, limit: 150 }),
      ]);
      setSummary(sm);
      setSessions(ss.sessions || []);
    } catch {
      toast.error("Failed to load visitor audit");
    } finally {
      setLoading(false);
    }
  }, [days, includeBots, sort, convertedOnly, checkoutOnly]);

  useEffect(() => { load(); }, [load]);

  const openDetail = (id) => { setActiveSession(id); setDialogOpen(true); };

  return (
    <div className="space-y-6" data-testid="visitor-audit">
      {/* KPIs */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-6">
        <StatTile icon={Users} label="Sessions" value={summary?.sessions ?? "—"} tint="blue" testid="audit-kpi-card" />
        <StatTile icon={ShoppingCart} label="Checkouts" value={summary?.checkouts_reached ?? "—"} sub={`${summary?.checkout_rate ?? 0}% of sessions`} tint="amber" testid="audit-kpi-card" />
        <StatTile icon={BadgeCheck} label="Conversions" value={summary?.conversions ?? "—"} tint="green" testid="audit-kpi-card" />
        <StatTile icon={TrendingUp} label="Conv. rate" value={`${summary?.conversion_rate ?? 0}%`} tint="violet" testid="audit-kpi-card" />
        <StatTile icon={Euro} label="Revenue" value={fmtMoney(summary?.revenue ?? 0, "EUR")} tint="green" testid="audit-kpi-card" />
        <StatTile icon={Euro} label="Avg order" value={fmtMoney(summary?.avg_order_value ?? 0, "EUR")} tint="slate" testid="audit-kpi-card" />
      </div>

      <ConversionSetup />

      {/* Sessions table */}
      <div className="rounded-xl border border-border bg-card" data-testid="audit-sessions-table">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-3">
          <h3 className="text-sm font-semibold">Visitor sessions</h3>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <Switch id="conv-only" checked={convertedOnly} onCheckedChange={setConvertedOnly} data-testid="filter-converted-only" />
              <Label htmlFor="conv-only" className="text-xs">Converted</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch id="checkout-only" checked={checkoutOnly} onCheckedChange={setCheckoutOnly} data-testid="filter-checkout-only" />
              <Label htmlFor="checkout-only" className="text-xs">Reached checkout</Label>
            </div>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="w-[150px]" data-testid="audit-sort-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="recent">Most recent</SelectItem>
                <SelectItem value="duration">Longest visit</SelectItem>
                <SelectItem value="pages">Most pages</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="icon" onClick={load} aria-label="Refresh" data-testid="audit-refresh-button"><RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /></Button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Time</TableHead>
                <TableHead>Location</TableHead>
                <TableHead className="hidden md:table-cell">IP</TableHead>
                <TableHead className="text-right">Duration</TableHead>
                <TableHead className="text-right">Pages</TableHead>
                <TableHead className="hidden lg:table-cell">Source</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Order</TableHead>
                <TableHead className="w-12" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}><TableCell colSpan={9}><Skeleton className="h-6 w-full" /></TableCell></TableRow>
                ))
              ) : sessions.length ? (
                sessions.map((s) => (
                  <TableRow key={s.session_id} className="cursor-pointer hover:bg-secondary/50" onClick={() => openDetail(s.session_id)} data-testid="audit-session-row">
                    <TableCell className="whitespace-nowrap text-xs text-muted-foreground">{fmtTime(s.last_seen_at)}</TableCell>
                    <TableCell className="text-xs"><span className="flex items-center gap-1.5"><span className="text-sm leading-none">{codeToFlag(s.country_code)}</span>{[s.city, s.country].filter(Boolean).join(", ") || "—"}</span></TableCell>
                    <TableCell className="hidden font-mono text-xs md:table-cell">{s.ip || "—"}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{fmtDuration(s.duration_ms)}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{s.pageviews}</TableCell>
                    <TableCell className="hidden max-w-[160px] truncate text-xs lg:table-cell">{s.source || "—"}</TableCell>
                    <TableCell><StatusBadge converted={s.converted} checkout={s.checkout_reached} /></TableCell>
                    <TableCell className="whitespace-nowrap text-right text-xs">{s.converted ? `${s.order_name || ""} ${fmtMoney(s.order_total, s.order_currency)}` : "—"}</TableCell>
                    <TableCell><Eye className="h-4 w-4 text-muted-foreground" /></TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow><TableCell colSpan={9} className="py-10 text-center text-sm text-muted-foreground">No sessions in this period</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <SessionDetailDialog sessionId={activeSession} open={dialogOpen} onOpenChange={setDialogOpen} />
    </div>
  );
};
